import React, { useContext, useState } from 'react';
import CopilotButton from '@components/Copilot/CopilotButton';
import { AgentId } from '@components/Copilot/types';
import { useAvCopilotContext } from '@context/AvCopilotProvider';
import {
  AvBreadcrumbs,
  AvTooltip,
  dateFormat,
  EVariant,
  FeatureFlags,
  formatDate,
  getDateFromFilter,
  getDatesFromDefinition,
  getInputValue,
  isDateRangeValue,
  NewRelativeDate,
  RelativeDatePeriod,
  RelativeDateType,
  validateValue,
} from '@fabric-ui/components';
import { Edit, MaximizeFullScreen as Maximize, MinimizeFullScreen as Minimize, More, Table } from '@fabric-ui/icons';
import { FilterActive } from '@fabric-ui/icons/colorful';
import { cellContentStyle, flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, capitalize, Divider, IconButton, useTheme } from '@mui/material';
import { HistoricDataIndicator } from '../../../components/Widgets/layout.components';
import { useAvContext } from '../../../context/AvContextProvider';
import { decodeAvalorQueryId, DwQueryResponse } from '../../../hooks/queryObjectAsync/useQueryObjectAsyncWithCachedResults.utils';
import { useIsIntuitiveHistoricalMode } from '../../../hooks/useIsIntuitiveHistoricalMode';
import { widgetTempIdInit } from '../constants';
import { CustomDashboardContext } from '../CustomDashboardContext';
import {
  activeIconStyle,
  floatingToolbarSx,
  getDrillDownBreadcrumbs,
  getDrillDownState,
  isTextWidget,
  shouldShowDrillDown,
} from '../CustomDashboardsUtils';
import { useGetDisplayName } from '../hooks/useGetDisplayName';
import { useGetFieldType } from '../hooks/useGetFieldType';
import { AllTypeWidgets, HistoricalFilterMode, TableSubType, TextSubType, TileSubType, WidgetCategory } from '../types';
import { TabsType } from '../types/types';
import { getParamFromURL } from './Clickthrough/utils';
import DrillUpButton from './DrillUpButton';
import { FilterToText } from './FiltersDescription';
import WidgetTitleInlineEdit from './WidgetTitleInlineEdit';
import WidgetToolbarActions from './WidgetToolbarActions';

const editIcon = <Edit style={iconSize(18)} />;
const maximizeIcon = <Maximize style={iconSize(18)} />;
const minimizeIcon = <Minimize style={iconSize(18)} />;
const moreIcon = <More style={iconSize(18)} />;
const filterActiveIcon = <FilterActive style={iconSize(18)} />;
const tableIcon = <Table style={{ ...iconSize(18) }} />;

export const getQueryHistoryDetectiveAdditionalParams = ({
  widgetId,
  dashboardId,
  queryId,
  timezone,
  widgetQueryResponse,
}: {
  widgetId?: string;
  dashboardId?: string;
  queryId?: string;
  timezone: string;
  widgetQueryResponse?: DwQueryResponse;
}) => ({
  widget_id: widgetId,
  dashboard_id: dashboardId,
  avalor_query_id: decodeAvalorQueryId(queryId),
  timezone,
  ...(widgetQueryResponse && { widget_query_response: widgetQueryResponse }),
});

export interface WidgetToolbarProps {
  widget: AllTypeWidgets;
  setWidget?: (newWidget: any) => void;
  isTable?: boolean;
  setIsTable?: any;
  queryId?: string;
  widgetQueryResponse?: DwQueryResponse;
}

const WidgetToolbar: React.FC<WidgetToolbarProps> = ({ widget, setWidget, isTable = false, setIsTable, queryId, widgetQueryResponse }) => {
  const {
    featureFlags,
    accountData: { aiFeatures },
  } = useAvContext();
  const isIntuitiveHistorical = useIsIntuitiveHistoricalMode();
  const theme = useTheme();
  const { setNewWidget, globalFilters, filters, setFilters, expandedWidgetId, setExpandedWidgetId, dashboardId } =
    useContext(CustomDashboardContext);
  const { isOpen: isCopilotOpen, onOpenCopilot, setIsNavigationBlockerOpen, lastSessionProps } = useAvCopilotContext();
  const isEditMode = getParamFromURL<boolean>(filters.isEditMode);
  const isMaximized = expandedWidgetId === widget.id;
  const isText = isTextWidget(widget);
  const isUnsavedWidget = Boolean(widget.id?.startsWith(widgetTempIdInit));
  const isQueryHistoryDetectiveOpenForWidget =
    isCopilotOpen &&
    lastSessionProps.agentId === AgentId.QUERY_HISTORY_DETECTIVE_AGENT &&
    lastSessionProps.additionalParams?.widget_id === widget.id;

  const [anchorEl, setAnchorEl] = useState<Element>();
  const {
    requests: [{ filter, top, sorting, projectionId, timeRange }],
  } = widget;

  const getDisplayName = useGetDisplayName(projectionId.name);
  const getFieldType = useGetFieldType(projectionId.name);
  const { drillDownHierarchy, dim, drilldownlist, showBreadcrumbs, drillUpLevelByIndex } = getDrillDownState({
    widget,
    setWidget,
    getDisplayName,
  });

  const onChangeFilter = (newValues: Record<string, any>) =>
    setFilters({
      ...filters,
      ...Object.fromEntries(Object.entries(newValues).map(([key, value]) => [key, Array.isArray(value) ? value : [value]])),
    });

  const handleFilter = () => {
    if (isEditMode) {
      setNewWidget(widget);
      onChangeFilter({ tab: TabsType.Filters, isEditWidgetOpen: true });
    }
  };
  const handleEdit = () => {
    const newWidget: AllTypeWidgets = {
      ...widget,
      ...(widget.drillDownHierarchy ? { drillDownHierarchy: { ...widget.drillDownHierarchy, activeIndex: 0 } } : {}),
      requests: widget.requests.map(r => ({ ...r, ...(r.top ? { top: { ...r.top, offset: 0 } } : r.top) })),
    };
    setNewWidget(newWidget);
    if (setWidget) {
      setWidget(newWidget);
    }
    onChangeFilter({ tab: TabsType.Data, isEditWidgetOpen: true });
  };

  const handleTitleChange = (newTitle: string) => {
    const updated = { ...widget, definition: { ...widget.definition, title: { title: newTitle } } } as AllTypeWidgets;
    setWidget?.(updated);
  };

  const handleExpand = () => setExpandedWidgetId(prev => (prev ? undefined : widget.id));
  const toggleShowAsTable = () => setIsTable?.(!isTable);

  const handleToggleQueryHistoryDetective = () => {
    if (isQueryHistoryDetectiveOpenForWidget) {
      setIsNavigationBlockerOpen(true);
    } else if (!isCopilotOpen) {
      onOpenCopilot({
        agentId: AgentId.QUERY_HISTORY_DETECTIVE_AGENT,
        additionalParams: getQueryHistoryDetectiveAdditionalParams({
          widgetId: widget.id,
          dashboardId,
          queryId,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          widgetQueryResponse,
        }),
        actions: [() => null],
        getConfirmDialogConfig: () => ({
          title: 'Discard investigation?',
          body: 'Closing this chat will discard the current investigation.',
        }),
      });
    }
  };

  const toggleUserMenu = ({ currentTarget }) => setAnchorEl(anchorEl ? undefined : currentTarget);
  const [closeTimeout, setCloseTimeout] = useState<any>();

  if (isText && !isEditMode) {
    return null;
  }

  const clearCloseTimeout = () => clearTimeout(closeTimeout);

  const closeMenu = () => {
    clearCloseTimeout();
    setCloseTimeout(setTimeout(() => setAnchorEl(undefined), 350));
  };

  const getFormattedDate = () => {
    if (timeRange?.relative) {
      return capitalize(
        `${RelativeDateType[(timeRange.relative as NewRelativeDate).periodBehaviour.toLowerCase()]} ${timeRange.relative.value} ${
          RelativeDatePeriod[timeRange.relative.unit.toLowerCase()]
        }`
      );
    }

    const dateObject = getDateFromFilter(timeRange ?? {});
    const isRange = Boolean(timeRange?.relative || timeRange?.between);
    const date = timeRange?.relative ? getDatesFromDefinition(dateObject.preset) : validateValue(dateObject.value, isRange);

    if (timeRange?.between) {
      return getInputValue({ date, isRange, preset: dateObject.preset });
    }

    return formatDate(isDateRangeValue(date) ? date?.to : undefined, dateFormat);
  };

  return (
    <Box
      sx={{
        ...flex.justifyBetween,
        ...(!isText && {
          pt: isMaximized ? 1.5 : 0,
          pr: isMaximized ? 0.5 : 0,
          pb: 0.25,
          gap: '16px',
        }),
      }}>
      <Box sx={{ ...flex.itemsCenter, gap: '10px', flex: 1 }}>
        {shouldShowDrillDown(widget) &&
          (widget.category !== WidgetCategory.Table || !widget.definition.custom.crossTableView) &&
          !!drillDownHierarchy.fields.length && <DrillUpButton widget={widget} />}
        {isText || showBreadcrumbs ? undefined : isEditMode ? (
          <WidgetTitleInlineEdit title={widget.definition.title.title} onChange={handleTitleChange} />
        ) : (
          <AvTooltip
            sx={{ fontSize: '18px', fontWeight: 600, ...cellContentStyle, WebkitLineClamp: '2', wordBreak: 'break-word', py: '2px' }}>
            {widget.definition.title.title}
          </AvTooltip>
        )}
        {showBreadcrumbs && (
          <Box className="draggableCancel" sx={{ ...flex.row, height: 'fit-content', flexWrap: 'wrap' }}>
            <AvBreadcrumbs
              breadcrumbs={getDrillDownBreadcrumbs({
                drillDownHierarchy,
                drilldownlist,
                dim,
                getDisplayName,
                getFieldType,
                onDrillUp: drillUpLevelByIndex,
              })}
              variant={EVariant.drillDown}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          ...flex.itemsCenter,
          gap: 1,
          ...(isText && floatingToolbarSx),
        }}
        className="draggableCancel">
        {isEditMode && (
          <AvTooltip title="Edit Widget">
            <IconButton className="showOnHover" onClick={handleEdit}>
              {editIcon}
            </IconButton>
          </AvTooltip>
        )}
        {![TableSubType.Table, TextSubType.Text, TileSubType.Tile].includes(widget.type as any) && !drillDownHierarchy.activeIndex && (
          <AvTooltip title="Table view">
            <IconButton
              className={!isTable ? 'showOnHover' : undefined}
              sx={isTable ? activeIconStyle(theme) : undefined}
              onClick={toggleShowAsTable}>
              {tableIcon}
            </IconButton>
          </AvTooltip>
        )}
        {!isText && !!timeRange && !!featureFlags[FeatureFlags.QueryHistoryDetectiveAgent] && !!aiFeatures?.enabled && (
          <CopilotButton
            isOpen={isQueryHistoryDetectiveOpenForWidget}
            agentId={AgentId.QUERY_HISTORY_DETECTIVE_AGENT}
            size="xSmall"
            sx={{ width: 34, minWidth: 34, px: 0 }}
            onClick={handleToggleQueryHistoryDetective}
            disabled={
              isUnsavedWidget ||
              (!widgetQueryResponse && !isQueryHistoryDetectiveOpenForWidget) ||
              (isCopilotOpen && !isQueryHistoryDetectiveOpenForWidget)
            }
            disabledToolTip={
              isUnsavedWidget
                ? 'Save the dashboard to use this feature'
                : isCopilotOpen && !isQueryHistoryDetectiveOpenForWidget
                  ? 'Close the active chat before starting a new one'
                  : 'Wait for the widget query to finish'
            }
          />
        )}
        {timeRange && (
          <HistoricDataIndicator
            hideText
            title={
              <span>
                Historical Data: {getFormattedDate()}
                {featureFlags[FeatureFlags.WidgetHistoricalFilterMode] && !isIntuitiveHistorical ? (
                  <>
                    <br />
                    Filter Evaluation Time: {widget.historicalFilterMode === HistoricalFilterMode.HISTORICAL ? 'Historical' : 'Current'}
                  </>
                ) : null}
              </span>
            }
            variant="primary"
          />
        )}

        {filter && (
          <AvTooltip title={<FilterToText filter={filter} top={top} sorting={sorting[0]} projectionId={projectionId.name} />}>
            <IconButton sx={isEditMode ? undefined : { cursor: 'default' }} onClick={handleFilter}>
              {filterActiveIcon}
            </IconButton>
          </AvTooltip>
        )}
        {(isEditMode || (!isText && !isEditMode)) && (
          <Box onMouseOver={clearCloseTimeout} onMouseLeave={closeMenu}>
            <AvTooltip title="More Options">
              <IconButton onClick={toggleUserMenu} sx={anchorEl ? activeIconStyle(theme) : undefined}>
                {moreIcon}
              </IconButton>
            </AvTooltip>
            {anchorEl && (
              <WidgetToolbarActions
                anchorEl={anchorEl}
                widget={widget}
                isEditMode={isEditMode}
                closeMenu={closeMenu}
                handleExpand={handleExpand}
                globalFilters={globalFilters}
              />
            )}
          </Box>
        )}
        {isMaximized && !isText && (
          <>
            <Divider orientation="vertical" sx={{ height: 18, alignSelf: 'center' }} />
            <AvTooltip title={isMaximized ? 'Minimize' : 'Full Screen'}>
              <IconButton onClick={handleExpand}>{isMaximized ? minimizeIcon : maximizeIcon}</IconButton>
            </AvTooltip>
          </>
        )}
      </Box>
    </Box>
  );
};

export default WidgetToolbar;
